import { useEffect, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from './useCurrentAppUser';
import { subDays } from 'date-fns';

export interface TaskNotification {
  id: string;
  title: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: string;
  created_at: string;
}

const QUERY_KEY = 'task-notifications-topbar';

export function useTaskNotifications() {
  const { data: appUser } = useCurrentAppUser();
  const queryClient = useQueryClient();
  const [readTaskIds, setReadTaskIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('read-task-notification-ids');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const appUserId = appUser?.id;

  const { data: taskNotifications = [] } = useQuery({
    queryKey: [QUERY_KEY, appUserId],
    queryFn: async (): Promise<TaskNotification[]> => {
      if (!appUserId) return [];

      const sevenDaysAgo = subDays(new Date(), 7).toISOString();

      const { data, error } = await supabase
        .from('tasks')
        .select('id, title, priority, status, created_at')
        .eq('assigned_to', appUserId)
        .in('status', ['pending', 'in_progress'])
        .gte('created_at', sevenDaysAgo)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('[useTaskNotifications] Error:', error);
        return [];
      }

      return (data ?? []) as TaskNotification[];
    },
    enabled: !!appUserId,
    staleTime: 2 * 60 * 1000,
  });

  const unreadTaskCount = taskNotifications.filter(t => !readTaskIds.has(t.id)).length;

  const markTaskAsRead = useCallback((taskId: string) => {
    setReadTaskIds(prev => {
      const next = new Set(prev);
      next.add(taskId);
      localStorage.setItem('read-task-notification-ids', JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Realtime subscription
  useEffect(() => {
    if (!appUserId) return;

    const channel = supabase
      .channel('task-notifications-topbar')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'tasks' },
        (payload) => {
          if ((payload.new as any)?.assigned_to === appUserId) {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY, appUserId] });
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'tasks' },
        (payload) => {
          if ((payload.new as any)?.assigned_to === appUserId) {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY, appUserId] });
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'tasks' },
        () => {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY, appUserId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [appUserId, queryClient]);

  return { taskNotifications, unreadTaskCount, markTaskAsRead };
}
